import { clearCaptureTransfers } from './capture-transfer';
import { decodeAgencyDisplayName, encodeAgencyDisplayName } from './agency-identity';
import { hasSubstanceExposure } from '@statura/utils';
import { z } from 'zod';
import { complianceEventFanout, type SirsConsideration } from '@statura/utils';
import { incidentLocalDateTimeToIso, incidentIsoToLocalInput } from './sirs-triage-time';

export const UNIFIED_INCIDENT_CAPTURE_FLAG = 'unified_incident_capture_web';
export const UNIFIED_CAPTURE_RETRY_PREFIX = 'unified-incident-capture:';
/** Clear retry snapshots for every actor in this tab on sign-out. */
export function clearUnifiedCaptureRetries(storage: Storage): void {
  const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
  for (const key of keys)
    if (key?.startsWith(UNIFIED_CAPTURE_RETRY_PREFIX)) storage.removeItem(key);
  clearCaptureTransfers(storage);
}
/** Exact regulated_actor_context cohort in 2951; capture only. */
export const UNIFIED_INCIDENT_CAPTURE_ROLES: readonly string[] = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'AGENCY_CONTRACTOR',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'LIFESTYLE_OFFICER',
];
export const UNIFIED_CAPTURE_CHIPS = [
  ['fall', 'Fall'],
  ['lifting', 'Hurt moving or lifting someone'],
  ['aggression', 'Aggression or behaviour'],
  ['needlestick', 'Needle-stick, sharps, blood or body fluid'],
  ['substance_exposure', 'Exposure to a substance'],
  ['medication', 'Medication'],
  ['skin_injury', 'Skin injury or wound'],
  ['unexplained_injury', 'Unexplained injury'],
  ['vehicle', 'Vehicle or road'],
  ['hazard', 'Hazard or near miss'],
  ['allegation', 'Allegation or inappropriate conduct'],
  ['restraint', 'Restraint used'],
  ['missing_person', 'Missing person'],
  ['death', 'Death'],
  ['outbreak', 'Infection or outbreak exposure'],
  ['not_sure', 'Not sure'],
] as const;
export type CapturePerson = {
  kind: 'CLIENT' | 'SELF' | 'STAFF' | 'AGENCY' | 'VISITOR';
  hurt: 'yes' | 'no' | 'not_sure';
  client_id?: string | null;
  staff_id?: string | null;
  display_name?: string | null;
  /** Agency worker's organisation; folded into display_name by the one agency shape. */
  organisation?: string | null;
  gender?: string | null;
  threatened?: boolean | null;
};
export type CaptureDraft = {
  people: CapturePerson[];
  what: string[];
  description: string;
  location: string;
  facilityId: string;
  when: 'now' | 'earlier' | 'estimated' | 'unknown';
  occurredLocal: string;
  /** Instant captured when Now was selected; absent on legacy in-memory drafts. */
  occurredNow?: string;
  awareLocal: string;
  immediateActions: string;
  care: Record<string, unknown>;
  whs: Record<string, unknown>;
};
/** Medication details (context entries) apply for the Medication chip, and, with additional types
 * on, when Medication error is the primary or an additional type (audit-J D9). The subtype stays
 * with the primary type only. */
export function medicationDetailsApply(
  draft: Pick<CaptureDraft, 'what' | 'care'>,
  typesEnabled: boolean,
): boolean {
  if (draft.what.includes('medication')) return true;
  if (!typesEnabled) return false;
  const additional = draft.care.additional_incident_types;
  return (
    draft.care.incident_type === 'MEDICATION_ERROR' ||
    (Array.isArray(additional) && additional.includes('MEDICATION_ERROR'))
  );
}
export function emptyCaptureDraft(clientId = '', facilityId = ''): CaptureDraft {
  return {
    people: clientId ? [{ kind: 'CLIENT', hurt: 'not_sure', client_id: clientId }] : [],
    what: [],
    description: '',
    location: '',
    facilityId,
    when: 'unknown',
    occurredLocal: '',
    occurredNow: '',
    awareLocal: '',
    immediateActions: '',
    care: {},
    whs: {},
  };
}

/** Field lists copied from compliance_event_hash_section, not arbitrary form keys.
 * No mutable defaults, timestamps, tenant, reporter, severity or derived fan-out. */
const FIELDS = {
  shared: [
    'occurred_at',
    'occurred_at_known',
    'occurred_at_certainty',
    'aware_at',
    'facility_id',
    'location',
    'what_happened',
    'immediate_actions',
  ],
  person: ['kind', 'hurt', 'client_id', 'staff_id', 'display_name', 'gender', 'threatened'],
  care: [
    'incident_type',
    'incident_subtype',
    'fall_observation',
    'fall_head_strike',
    'fall_away_unsupervised',
    'treatment_required',
    'police_notified',
    'police_reasonable_grounds',
    'consider_sirs',
    'program_context_revision',
    'program_lens',
    'program_selection',
    'resident_details',
    'client_id',
  ],
  whs: [
    'incident_type',
    'notifiability_facts',
    'worker_injury_details',
    'body_part',
    'first_aid',
    'medical_treatment',
  ],
} as const;
function project(value: Record<string, unknown>, fields: readonly string[]) {
  return Object.fromEntries(fields.map((key) => [key, value[key] ?? null]));
}
function localInstant(value: string, timezone: string) {
  const [date, time] = value.split('T');
  return incidentLocalDateTimeToIso(date ?? '', time ?? '', timezone);
}
function capturePeople(people: CapturePerson[]) {
  return people.filter(
    (p) =>
      !(
        ['STAFF', 'AGENCY', 'VISITOR'].includes(p.kind) &&
        p.hurt === 'no' &&
        !p.client_id &&
        !p.staff_id &&
        !p.display_name?.trim()
      ),
  );
}
/** Mirror the controlled capture command's pruning and unlinked-resident override. */
export function captureFanout(draft: CaptureDraft) {
  const people = capturePeople(draft.people);
  const fanout = complianceEventFanout({ ...draft, people });
  if (people.some((p) => p.kind === 'CLIENT' && !p.client_id && p.display_name?.trim()))
    return {
      ...fanout,
      care: true,
      reasons: [
        ...fanout.reasons,
        'The office needs a care record to link the named resident or client.',
      ],
    };
  return fanout;
}
export function captureDraftProblem(
  draft: CaptureDraft,
  section: 'all' | 'people' | 'description' = 'all',
) {
  if (section !== 'people') {
    if (!draft.description.trim()) return 'Describe what happened to send this incident report.';
    if (['earlier', 'estimated'].includes(draft.when) && !draft.occurredLocal)
      return 'Enter when this incident happened, or choose Not known.';
  }
  if (section === 'description') return null;
  if (draft.people.some((p) => p.kind === 'CLIENT' && !p.client_id && !p.display_name?.trim()))
    return 'Choose a resident or client, or type their name.';
  if (
    draft.people.some(
      (p) =>
        ['STAFF', 'AGENCY', 'VISITOR'].includes(p.kind) &&
        p.hurt !== 'no' &&
        !p.staff_id &&
        !p.display_name?.trim(),
    )
  )
    return 'Choose a person or type a name for anyone hurt or whose injury is not yet known.';
  return null;
}
export function buildUnifiedCapturePayload(
  draft: CaptureDraft,
  operationId: string,
  timezone: string,
  now: string,
  typesEnabled = false,
) {
  const problem = captureDraftProblem(draft);
  if (problem) throw new Error(problem);
  const fanout = captureFanout(draft);
  const occurred =
    draft.when === 'now'
      ? draft.occurredNow || now
      : ['earlier', 'estimated'].includes(draft.when)
        ? localInstant(draft.occurredLocal, timezone)
        : null;
  const awareness = draft.awareLocal ? localInstant(draft.awareLocal, timezone) : null;
  if (
    [occurred, awareness].some((value) => value && Date.parse(value) > Date.parse(now) + 5 * 60_000)
  )
    throw new Error('The incident and provider awareness times cannot be in the future.');
  // Prune conditional answers before projection so hidden fields never reach SQL.
  const care = { ...draft.care };
  if (
    !typesEnabled &&
    (care.incident_type === 'MEDICAL_EPISODE' || care.additional_incident_types !== undefined)
  )
    throw new Error('These incident types are not available yet. Reload the report.');
  const whs = { ...draft.whs };
  if (!medicationDetailsApply(draft, typesEnabled)) delete care.medication_event_context;
  if (typesEnabled) {
    // Explicit primary selection. Extra types never participate in fan-out or SIRS.
  } else if (!draft.what.includes('fall') && !draft.what.includes('medication')) {
    delete care.incident_type;
    delete care.incident_subtype;
  } else if (care.incident_type) {
    care.incident_type = draft.what.includes('fall') ? 'FALL' : 'MEDICATION_ERROR';
    // A subtype from the previous taxonomy branch cannot be reused.
    if (care.incident_type !== draft.care.incident_type) delete care.incident_subtype;
  }
  if (typesEnabled ? care.incident_type !== 'FALL' : !draft.what.includes('fall'))
    for (const key of ['fall_observation', 'fall_head_strike', 'fall_away_unsupervised'])
      delete care[key];
  if (!draft.people.some((p) => p.kind !== 'CLIENT' && p.hurt !== 'no'))
    for (const key of ['worker_injury_details', 'body_part', 'first_aid', 'medical_treatment'])
      delete whs[key];
  // The category is descriptive only; the independent outcome facts drive review.
  if (draft.what.includes('vehicle')) whs.incident_type = 'VEHICLE';
  else if (draft.what.includes('needlestick')) whs.incident_type = 'NEEDLE_STICK';
  else if (draft.what.includes('substance_exposure')) whs.incident_type = 'EXPOSURE';
  if (hasSubstanceExposure(draft.what)) {
    whs.notifiability_facts = {
      medical_treatment_after_substance_exposure:
        whs.exposureTreatment === 'YES' ? true : whs.exposureTreatment === 'NO' ? false : null,
      ...(whs.notifiability_facts as object),
    };
  } else {
    if (whs.notifiability_facts && typeof whs.notifiability_facts === 'object') {
      whs.notifiability_facts = { ...(whs.notifiability_facts as Record<string, unknown>) };
      delete (whs.notifiability_facts as Record<string, unknown>)
        .medical_treatment_after_substance_exposure;
    }
  }
  if (typesEnabled && care.incident_type !== 'MEDICATION_ERROR') delete care.incident_subtype;
  const projectedCare = project(fanout.care ? care : {}, FIELDS.care);
  if (typesEnabled && fanout.care && care.additional_incident_types !== undefined)
    projectedCare.additional_incident_types = care.additional_incident_types;
  if (!typesEnabled && projectedCare.incident_type === 'MEDICAL_EPISODE')
    throw new Error('Medical episode is not available yet. Reload the report.');
  // Optional evidence travels with the frozen draft, outside the core command hash.
  if (fanout.care && care.medication_event_context !== undefined)
    projectedCare.medication_event_context = care.medication_event_context;
  return {
    schema_version: 1 as const,
    operation_id: operationId,
    shared: project(
      {
        occurred_at: occurred,
        occurred_at_known: draft.when !== 'unknown',
        occurred_at_certainty:
          draft.when === 'unknown' ? 'UNKNOWN' : draft.when === 'estimated' ? 'ESTIMATED' : 'KNOWN',
        aware_at: awareness,
        facility_id: draft.facilityId || null,
        location: draft.location || null,
        what_happened: draft.description.trim(),
        immediate_actions: draft.immediateActions || null,
      },
      draft.when === 'unknown'
        ? FIELDS.shared.filter((field) => field !== 'occurred_at')
        : FIELDS.shared,
    ),
    people: capturePeople(draft.people).map((p) =>
      project(
        p.kind === 'AGENCY' && p.display_name?.trim()
          ? { ...p, display_name: encodeAgencyDisplayName(p.display_name, p.organisation) }
          : p,
        FIELDS.person,
      ),
    ),
    what: [...draft.what],
    care: projectedCare,
    whs: project(fanout.whs ? whs : {}, FIELDS.whs),
  };
}
export type UnifiedCapturePayload = ReturnType<typeof buildUnifiedCapturePayload>;

/** Restore editable answers too, while retry transport retains the original snapshot. */
export function captureDraftFromPayload(
  payload: UnifiedCapturePayload,
  timezone: string,
): CaptureDraft {
  const text = (value: unknown) => (typeof value === 'string' ? value : '');
  return {
    ...emptyCaptureDraft(),
    people: (payload.people as unknown as CapturePerson[]).map((p) => {
      const agency = p.kind === 'AGENCY' ? decodeAgencyDisplayName(p.display_name) : null;
      return agency ? { ...p, display_name: agency.name, organisation: agency.organisation } : p;
    }),
    what: payload.what,
    description: text(payload.shared.what_happened),
    location: text(payload.shared.location),
    facilityId: text(payload.shared.facility_id),
    immediateActions: text(payload.shared.immediate_actions),
    when:
      payload.shared.occurred_at_certainty === 'UNKNOWN'
        ? 'unknown'
        : payload.shared.occurred_at_certainty === 'ESTIMATED'
          ? 'estimated'
          : 'earlier',
    occurredLocal: payload.shared.occurred_at
      ? incidentIsoToLocalInput(text(payload.shared.occurred_at), timezone)
      : '',
    awareLocal: payload.shared.aware_at
      ? incidentIsoToLocalInput(text(payload.shared.aware_at), timezone)
      : '',
    care: payload.care,
    whs: payload.whs,
  };
}

/** Stable comparison material for the Hash contract, not the database's md5.
 * SQL owns request_hash. Object keys sorted, array order and time text retained. */
export function canonicalCaptureJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalCaptureJson).join(',')}]`;
  if (value && typeof value === 'object')
    return `{${Object.keys(value)
      .sort()
      .map(
        (key) =>
          `${JSON.stringify(key)}:${canonicalCaptureJson((value as Record<string, unknown>)[key])}`,
      )
      .join(',')}}`;
  return JSON.stringify(value ?? null);
}
const legSchema = z
  .object({
    state: z.enum(['saved', 'pending', 'not_required']),
    id: z.string().uuid().nullable(),
    reference: z.string().nullable(),
    code: z.string().nullable(),
  })
  .strict()
  .refine((leg) => leg.state !== 'saved' || leg.id !== null, {
    message: 'A saved incident receipt needs its record id.',
  });
export const unifiedReceiptSchema = z
  .object({
    ok: z.literal(true),
    medication_context_saved: z.boolean().optional(),
    operation_id: z.string().uuid(),
    care: legSchema,
    whs: legSchema,
    awareness_time_unconfirmed: z.boolean().optional(),
    resident_death_reported: z.boolean().optional(),
    linked: z.boolean(),
    pending_leg_ids: z.array(z.string().uuid()),
  })
  .strict();
export type UnifiedCaptureReceipt = z.infer<typeof unifiedReceiptSchema>;
export type UnifiedCaptureResult =
  | { success: true; receipt: UnifiedCaptureReceipt }
  | { success: false; error: string; retryable?: boolean };
const CAPTURE_ERROR_PREFIXES = [
  'compliance_event_operation_changed',
  'compliance_event_operation_in_progress',
  'compliance_event_resource_not_in_tenant',
  'compliance_event_fanout_empty',
  'compliance_event_occurred_at_in_future',
  'compliance_event_leg_provenance',
  'compliance_event_leg_key_preseeded',
  'compliance_event_field_not_allowed',
] as const;
/** Allowlisted prefix only. Never return or log the raw SQL message. */
export function unifiedCaptureMessagePrefix(message?: string) {
  return CAPTURE_ERROR_PREFIXES.find((prefix) => message?.startsWith(prefix)) ?? 'unknown';
}
export function unifiedCaptureRetryable(code?: string, message?: string): boolean {
  if (['22023', '42501', 'PGRST202', '42883', '404'].includes(code ?? '')) return false;
  if (
    code === '55000' &&
    [
      'compliance_event_operation_changed',
      'compliance_event_resource_not_in_tenant',
      'compliance_event_fanout_empty',
      'compliance_event_leg_provenance',
      'compliance_event_leg_key_preseeded',
      'compliance_event_field_not_allowed',
    ].includes(unifiedCaptureMessagePrefix(message))
  )
    return false;
  return true;
}
export function unifiedCaptureError(code?: string, message?: string): string {
  if (['PGRST202', '42883', '404'].includes(code ?? ''))
    return "This form isn't available yet. Use the standard form.";
  if (
    code === '22023' &&
    unifiedCaptureMessagePrefix(message) === 'compliance_event_occurred_at_in_future'
  )
    return "The time is later than now on the server. Check this device's clock, or choose Earlier and enter the time.";
  if (code === '22023')
    return 'The incident details could not be verified. Check the people, location and times, then change the answers before sending again.';
  if (code === '55000') {
    switch (unifiedCaptureMessagePrefix(message)) {
      case 'compliance_event_operation_changed':
        return 'These answers differ from an earlier submission. Ask the office to check that submission. Change an answer to send a new report.';
      case 'compliance_event_resource_not_in_tenant':
        return 'A selected person or service location is unavailable for your provider. Choose them again or type a person’s name before sending.';
      case 'compliance_event_fanout_empty':
        return 'These answers do not identify an incident record to save. Check who was involved and what happened before sending.';
      case 'compliance_event_operation_in_progress':
        return 'This incident report is still being processed. Try again to check the same report.';
      case 'compliance_event_leg_provenance':
      case 'compliance_event_leg_key_preseeded':
        return 'This report could not be saved because its record identity could not be verified. Ask the office to check it before sending again.';
      case 'compliance_event_field_not_allowed':
        return 'This report contains a detail that cannot be saved in this form. Check the answers or ask the office for help before sending again.';
    }
  }
  if (code === '42501')
    return 'Your access to send this incident report could not be confirmed. Ask the office to check your provider account.';
  if (code === '28000') return 'Sign in again, then retry this incident report.';
  return 'This incident receipt could not be confirmed. Try again with the same report.';
}
export { whsSiteWordingKey, whsSiteWording } from '@statura/utils';
export function careNeedsUrgentReview(
  care: Record<string, unknown>,
  chips: readonly string[] = [],
) {
  const consideration = (care.consider_sirs ?? {}) as SirsConsideration;
  return (
    chips.some((chip) => ['allegation', 'missing_person', 'death', 'restraint'].includes(chip)) ||
    care.treatment_required === 'YES' ||
    care.police_reasonable_grounds === 'YES' ||
    ['YES', 'UNSURE'].includes(consideration.possible_neglect ?? '') ||
    ['YES', 'UNSURE'].includes(consideration.possible_force ?? '') ||
    ['YES', 'UNSURE'].includes(consideration.another_resident ?? '')
  );
}
